import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Row, Col, Card, Statistic, Table, Tag, Button, Space, Modal, Form, Input, Tooltip, App, notification, Badge, List, Empty } from 'antd';
import { UserOutlined, LogoutOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, SettingOutlined, MonitorOutlined, MailOutlined, LockOutlined, TeamOutlined, ApiOutlined, DashboardOutlined, SearchOutlined, DatabaseOutlined, BellOutlined, SoundOutlined, MutedOutlined, NotificationOutlined, PauseCircleOutlined, LineChartOutlined, EyeOutlined, EyeInvisibleOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, WifiOutlined } from '@ant-design/icons';
import { targetsAPI, alertsAPI, influxTargetsAPI } from '../services/api';
import socketService from '../services/socket';
import TargetStats from '../components/TargetStats';
import LatencyChart from '../components/LatencyChart';
import TargetDetailAlerts from '../components/TargetDetailAlerts';
import UnifiedFilters from '../components/UnifiedFilters';
import ExportLogsButton from '../components/ExportLogsButton';
import EmailNotifications from '../components/EmailNotifications';
import EmailConfig from '../components/EmailConfig';
import UserManagement from '../components/UserManagement';
import ChangePassword from '../components/ChangePassword';
import Settings from '../components/Settings';
import TargetForm from '../components/TargetForm';
import Agents from './Agents';
import AgentDetail from './AgentDetail';
import MonitoringOverview from './MonitoringOverview';
import InfluxDBTargets from './InfluxDBTargets';
import { toLocalTime, toLocalTimeWithTZ } from '../utils/timezone';
import { playAlertAudio, resolveRingtoneUrl, volumeForSeverity } from '../utils/alertSound';
import {
  bulletLine,
  formatInfluxAlertTitle,
  formatInfluxPopupTitle,
  formatInfluxAnomalyDescription,
  normalizeInfluxAnomalyMessage,
  formatTargetAlertTitle,
  formatTargetPopupTitle,
  getAlertStatusIcon,
  monitoredItemTypeLabel,
  resolveAlertPresentation,
  severityIssuePrefix,
  buildTargetStatusPopupDescription,
  TargetStatusPopupDescription,
  shouldNotifyTargetStatus,
  shouldNotifyInfluxStatus,
  normalizeTimeoutDownMessage,
} from '../utils/alertDisplay';
import {
  acknowledgeAlertKey,
  acknowledgeAllAlertKeys,
  clearAcknowledgedKeysForTarget,
  clearInfluxAckOnStatusChange,
  prepareInfluxAlertAck,
  clearHardwareAckWhenHealthy,
  getAlertDedupeKey,
  countAlertBadge,
  syncFailureAlertsFromTargets,
  mapServerAlert,
  mergeAlerts,
  upsertAlert,
  removeActiveAlertsForTargetId,
  removeActiveAlertsForHardwareAgent,
  removeActiveAlertsForAgent,
  removeMonitoredItemAlerts,
  applyResourceMetricsToAlerts,
  isEventAnomalyClear,
  pruneRecoveredMonitorAlerts,
  pruneRecoveredInfluxAlerts,
  patchTargetInList,
  buildInfluxStatusMap,
} from '../utils/alertDedupe';
import {
  PHASE1_ALERT_TYPES,
  registerPhase1Incident,
  markPhase1IncidentAcknowledged,
  markAllPhase1IncidentsAcknowledged,
  phase1AckRequestFromAlert,
  shouldShowAckButton,
  countPendingAcknowledgements,
  canShowPhase1Popup,
  recordPhase1PopupShown,
  clearPhase1OnTargetRecovery,
  clearPhase1OnInfluxRecovery,
  clearPhase1OnHardwareRecovery,
  isPhase1FailureStatus,
} from '../utils/incidentAck';
import { isInfluxUpStatus } from '../utils/influxStatus';
import { parseApiData } from '../utils/apiResponse';
import { configureAlertNotifications } from '../utils/alertPrefs';
import './Dashboard.css';

const { Header, Content, Sider } = Layout;

const CellEllipsis = ({ text, width = 200, style }) => {
  if (!text || text === '-') {
    return <span className="target-table-muted">-</span>;
  }
  return (
    <Tooltip title={text}>
      <div className="target-table-ellipsis" style={{ maxWidth: width, ...style }}>
        {text}
      </div>
    </Tooltip>
  );
};

const isActiveMonitorTarget = (target) =>
  target?.is_active !== false && target?.is_active !== 0 && target?.is_active !== '0';

const getMonitorTargetStatus = (target) => {
  const raw = target?.last_status ?? target?.status;
  return raw ? String(raw).toUpperCase() : null;
};

const MONITOR_CARD_BODY = { padding: '12px 16px' };
const MONITOR_CARD_STYLE = { marginBottom: 0 };

const monitorFilterCardStyle = (active, accent) => ({
  ...MONITOR_CARD_STYLE,
  position: 'relative',
  border: active ? `2px solid ${accent}` : undefined,
  boxShadow: active ? `0 0 0 2px ${accent}33` : undefined,
});

const monitorFilterActiveDot = (active, accent) => active ? (
  <div
    style={{
      position: 'absolute',
      top: 8,
      right: 8,
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: accent,
    }}
  />
) : null;

const getDndUntil = () => {
  try {
    const v = localStorage.getItem('pingpulse_dnd_until');
    return v ? parseInt(v, 10) : 0;
  } catch {
    return 0;
  }
};

const Dashboard = ({ user, onLogout }) => {
  const { message } = App.useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [targets, setTargets] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [statsModalVisible, setStatsModalVisible] = useState(false);
  const [statsPopupHidden, setStatsPopupHidden] = useState(true);
  const [currentFilters, setCurrentFilters] = useState({ timeRange: '5m', status: 'all' });
  const [targetLogs, setTargetLogs] = useState([]);
  const [targetLogsLoading, setTargetLogsLoading] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('monitor');
  const [collapsed, setCollapsed] = useState(false);
  const [targetSearch, setTargetSearch] = useState('');
  const [monitorStatusFilter, setMonitorStatusFilter] = useState(null); // 'ACTIVE' | 'UP' | 'DOWN' | 'TIMEOUT' | null
  const [form] = Form.useForm();
  const [activeAlerts, setActiveAlerts] = useState(() => {
    try {
      const saved = localStorage.getItem('pingpulse_active_alerts');
      return saved ? mergeAlerts(JSON.parse(saved)) : [];
    } catch { return []; }
  });
  const [alertPanelVisible, setAlertPanelVisible] = useState(false);
  const [alertSoundEnabled, setAlertSoundEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem('pingpulse_alert_sound');
      return saved !== 'false';
    } catch { return true; }
  });
  const [alertPopupEnabled, setAlertPopupEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem('pingpulse_alert_popup');
      return saved !== 'false';
    } catch { return true; }
  });
  const [dndUntil, setDndUntil] = useState(getDndUntil);
  const [ackUIVersion, setAckUIVersion] = useState(0);
  const currentFiltersRef = useRef(currentFilters);
  const targetsRef = useRef(targets);
  const influxStatusRef = useRef({});
  const statsModalVisibleRef = useRef(statsModalVisible);
  const selectedTargetIdRef = useRef(selectedTarget?.id ?? null);
  const statsPopupHiddenRef = useRef(true);
  const lastTargetPopupRef = useRef({});
  const statsModalSessionRef = useRef(null);
  const targetLogsFetchGenRef = useRef(0);
  const activeAlertsRefreshInFlightRef = useRef(false);
  const activeAlertsRefreshTimerRef = useRef(null);
  const ACTIVE_ALERTS_POLL_MS = 60000;
  const ACTIVE_ALERTS_DEBOUNCE_MS = 2000;

  const isDndActive = dndUntil > Date.now();

  useEffect(() => {
    configureAlertNotifications();
  }, []);

  const showGroupedPopup = useCallback((key, type, config) => {
    if (!alertPopupEnabled || getDndUntil() > Date.now()) {
      return;
    }
    notification.open({
      key,
      type,
      placement: 'topRight',
      ...config,
    });
  }, [alertPopupEnabled]);

  const enableDoNotDisturb = useCallback((minutes = 60) => {
    const until = Date.now() + minutes * 60 * 1000;
    localStorage.setItem('pingpulse_dnd_until', String(until));
    setDndUntil(until);
    notification.destroy();
    message.info(`Popup notifications muted for ${minutes} minutes`);
  }, [message]);

  const disableDoNotDisturb = useCallback(() => {
    localStorage.removeItem('pingpulse_dnd_until');
    setDndUntil(0);
    message.success('Popup notifications enabled');
  }, [message]);

  // Persist activeAlerts to localStorage on change
  useEffect(() => {
    localStorage.setItem('pingpulse_active_alerts', JSON.stringify(activeAlerts));
  }, [activeAlerts]);

  // Persist alert sound preference
  useEffect(() => {
    localStorage.setItem('pingpulse_alert_sound', String(alertSoundEnabled));
  }, [alertSoundEnabled]);

  // Persist alert popup preference
  useEffect(() => {
    localStorage.setItem('pingpulse_alert_popup', String(alertPopupEnabled));
  }, [alertPopupEnabled]);

  const playAlertSound = useCallback((severity) => {
    if (!alertSoundEnabled) return;
    const url = resolveRingtoneUrl();
    const volume = volumeForSeverity(severity);
    playAlertAudio(url, volume).then((ok) => {
      if (!ok) {
        console.warn('Failed to play alert sound:', url);
      }
    });
  }, [alertSoundEnabled]);

  const refreshActiveAlerts = useCallback(async () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    if (activeAlertsRefreshInFlightRef.current) return;

    activeAlertsRefreshInFlightRef.current = true;
    try {
      let influxStatusMap = { ...influxStatusRef.current };
      try {
        const influxRows = parseApiData(await influxTargetsAPI.getAll(), []);
        if (Array.isArray(influxRows)) {
          influxStatusMap = buildInfluxStatusMap(influxRows, influxStatusMap);
          influxStatusRef.current = influxStatusMap;
        }
      } catch (influxErr) {
        console.warn('Failed to load Influx targets for alert sync:', influxErr);
      }

      const localAlerts = JSON.parse(localStorage.getItem('pingpulse_active_alerts') || '[]');
      const serverRows = await alertsAPI.getActive(50);
      const serverAlerts = (Array.isArray(serverRows)
        ? serverRows
        : Array.isArray(serverRows?.data)
          ? serverRows.data
          : []
      )
        .map(mapServerAlert)
        .filter((alert) => {
          if (alert.type === 'target-status') {
            const tid = alert.data?.targetId ?? alert.targetId;
            const row = targetsRef.current.find((t) => String(t.id) === String(tid));
            const liveStatus = String(row?.last_status ?? row?.status ?? '').toUpperCase();
            if (liveStatus === 'UP' || liveStatus === 'OK') return false;
            return shouldNotifyTargetStatus({
              status: alert.data?.status ?? alert.status,
              isRecovery: alert.data?.isRecovery,
              previousStatus: alert.data?.previousStatus,
            });
          }
          if (alert.type === 'influxdb') {
            const d = alert.data || {};
            const tid = String(
              alert.targetId ?? d.targetId ?? d.target_id ?? d.influxTargetId ?? ''
            );
            const liveStatus = influxStatusMap[tid] ?? influxStatusRef.current[tid];
            if (liveStatus && isInfluxUpStatus(liveStatus)) return false;
            return shouldNotifyInfluxStatus(alert.data?.status ?? alert.status);
          }
          return true;
        });
      const serverKeys = new Set(serverAlerts.map(getAlertDedupeKey));
      const localOnly = localAlerts.filter((la) => !serverKeys.has(getAlertDedupeKey(la)));
      setActiveAlerts((prev) => {
        let merged = mergeAlerts(prev, serverAlerts, localOnly);
        merged = pruneRecoveredMonitorAlerts(merged, targetsRef.current);
        merged = pruneRecoveredInfluxAlerts(merged, influxStatusMap);
        merged = syncFailureAlertsFromTargets(targetsRef.current, merged);
        return merged;
      });
    } catch (err) {
      console.error('Failed to load server alerts:', err);
    } finally {
      activeAlertsRefreshInFlightRef.current = false;
    }
  }, []);

  const refreshActiveAlertsRef = useRef(refreshActiveAlerts);
  useEffect(() => {
    refreshActiveAlertsRef.current = refreshActiveAlerts;
  }, [refreshActiveAlerts]);

  const scheduleRefreshActiveAlerts = useCallback((delayMs = ACTIVE_ALERTS_DEBOUNCE_MS) => {
    if (activeAlertsRefreshTimerRef.current) {
      clearTimeout(activeAlertsRefreshTimerRef.current);
    }
    activeAlertsRefreshTimerRef.current = window.setTimeout(() => {
      activeAlertsRefreshTimerRef.current = null;
      refreshActiveAlertsRef.current?.();
    }, delayMs);
  }, []);

  // Sync alerts from server — poll slowly; socket + debounced refresh handle realtime
  useEffect(() => {
    refreshActiveAlerts();
    const timer = setInterval(() => {
      if (!document.hidden) refreshActiveAlerts();
    }, ACTIVE_ALERTS_POLL_MS);
    const onVisible = () => {
      if (!document.hidden) scheduleRefreshActiveAlerts(500);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      if (activeAlertsRefreshTimerRef.current) {
        clearTimeout(activeAlertsRefreshTimerRef.current);
        activeAlertsRefreshTimerRef.current = null;
      }
    };
  }, [refreshActiveAlerts, scheduleRefreshActiveAlerts]);

  const agentDetailMatch = location.pathname.match(/^\/agents\/(.+)$/);
  const agentDetailId = agentDetailMatch ? agentDetailMatch[1] : null;
  const isAgentPage = location.pathname === '/agents' || location.pathname.startsWith('/agents/');
  const isOverviewPage = location.pathname === '/overview';
  const isInfluxPage = location.pathname === '/influxdb';

  // Handle target query parameter from Overview page
  const [searchParams] = useSearchParams();
  const targetParam = searchParams.get('target');

  // Sync selectedMenu with URL for settings pages
  useEffect(() => {
    if (location.pathname === '/settings' && selectedMenu !== 'settings') {
      setSelectedMenu('settings');
    } else if (location.pathname === '/settings/email' && selectedMenu !== 'email-config') {
      setSelectedMenu('email-config');
    } else if (location.pathname === '/settings/notifications' && selectedMenu !== 'email-notifications') {
      setSelectedMenu('email-notifications');
    } else if (location.pathname === '/settings/users' && selectedMenu !== 'user-management') {
      setSelectedMenu('user-management');
    } else if (location.pathname === '/settings/password' && selectedMenu !== 'change-password') {
      setSelectedMenu('change-password');
    }
  }, [location.pathname, selectedMenu]);

  useEffect(() => {
    if (isAgentPage && selectedMenu !== 'agents') {
      setSelectedMenu('agents');
    } else if (isOverviewPage && selectedMenu !== 'overview') {
      setSelectedMenu('overview');
    } else if (isInfluxPage && selectedMenu !== 'influxdb') {
      setSelectedMenu('influxdb');
    }
  }, [isAgentPage, isOverviewPage, isInfluxPage, selectedMenu]);

  // Handle target selection from query parameter (Overview page)
  useEffect(() => {
    if (targetParam && targets.length > 0) {
      const target = targets.find(t => t.id === parseInt(targetParam));
      if (target) {
        setSelectedTarget(target);
        setStatsModalVisible(true);
        // Clear the URL parameter after selecting
        navigate('/', { replace: true });
      }
    }
  }, [targetParam, targets, navigate]);

  const handleMenuClick = ({ key }) => {
    setSelectedMenu(key);
    if (key === 'agents') {
      navigate('/agents');
    } else if (key === 'overview') {
      navigate('/overview');
    } else if (key === 'influxdb') {
      navigate('/influxdb');
    } else if (key === 'monitor') {
      navigate('/');
    } else if (key === 'settings') {
      navigate('/settings');
    } else if (key === 'email-config') {
      navigate('/settings/email');
    } else if (key === 'email-notifications') {
      navigate('/settings/notifications');
    } else if (key === 'user-management') {
      navigate('/settings/users');
    } else if (key === 'change-password') {
      navigate('/settings/password');
    }
  };

  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  }, [currentFilters]);

  useEffect(() => {
    targetsRef.current = targets;
  }, [targets]);

  useEffect(() => {
    statsModalVisibleRef.current = statsModalVisible;
    selectedTargetIdRef.current = selectedTarget?.id ?? null;
    statsPopupHiddenRef.current = statsPopupHidden;
  }, [statsModalVisible, selectedTarget?.id, statsPopupHidden]);

  useEffect(() => {
    if (!statsModalVisible || !selectedTarget?.id) {
      if (!statsModalVisible) statsModalSessionRef.current = null;
      return;
    }

    const targetId = selectedTarget.id;
    const sessionKey = String(targetId);
    if (statsModalSessionRef.current !== sessionKey) {
      statsModalSessionRef.current = sessionKey;
      notification.destroy(`target-alert-${targetId}`);
      setStatsPopupHidden(true);
      statsPopupHiddenRef.current = true;
    }

    if (!lastTargetPopupRef.current[targetId] && selectedTarget.last_status) {
      const targetName = selectedTarget.name;
      const isRecovery = selectedTarget.last_status === 'UP';
      const popupData = {
        status: selectedTarget.last_status,
        timestamp: selectedTarget.last_check ? toLocalTime(selectedTarget.last_check) : null,
        host: selectedTarget.host,
        port: selectedTarget.port,
        errorMessage: selectedTarget.last_error,
        message: selectedTarget.last_message,
      };
      const popupDesc = buildTargetStatusPopupDescription(popupData, selectedTarget, { isRecovery });
      const popupDescNode = (
        <TargetStatusPopupDescription
          data={popupData}
          target={selectedTarget}
          isRecovery={isRecovery}
        />
      );
      let popupType = 'info';
      if (selectedTarget.last_status === 'DOWN') popupType = 'error';
      else if (selectedTarget.last_status === 'TIMEOUT') popupType = 'warning';
      else if (isRecovery) popupType = 'success';

      lastTargetPopupRef.current[targetId] = {
        popupKey: `target-alert-${targetId}`,
        popupType,
        popupTitle: formatTargetPopupTitle(selectedTarget.last_status, targetName, isRecovery),
        popupDesc,
        popupDescNode,
        duration: selectedTarget.last_status === 'DOWN' ? 0 : 12,
      };
    }
  }, [
    statsModalVisible,
    selectedTarget?.id,
    selectedTarget?.name,
    selectedTarget?.host,
    selectedTarget?.port,
    selectedTarget?.last_status,
    selectedTarget?.last_check,
    selectedTarget?.last_error,
    selectedTarget?.last_message,
    selectedTarget?.last_latency,
  ]);

  const openTargetDetailModal = useCallback((targetId) => {
    const t = targetsRef.current.find((x) => x.id === targetId);
    if (t) {
      setSelectedTarget(t);
      setStatsModalVisible(true);
    }
  }, []);

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await targetsAPI.getTargets();
      const rows = parseApiData(response, []);
      if (Array.isArray(rows)) {
        setTargets(rows);
        setActiveAlerts((prev) => {
          let next = syncFailureAlertsFromTargets(rows, prev);
          next = pruneRecoveredMonitorAlerts(next, rows);
          return next;
        });
      }
      if (response?.success === false) {
        message.error(response.message || 'Gagal mengambil data targets');
      }
    } catch (error) {
      message.error('Gagal mengambil data targets');
    } finally {
      setLoading(false);
    }
  }, [message]);

  const fetchDashboardStats = useCallback(async (filters = null) => {
    try {
      // Use provided filters or current filters
      const filterToUse = filters || currentFiltersRef.current;

      const response = await targetsAPI.getDashboardStats(filterToUse);
      if (response.success) {
        setDashboardStats(response.data);
      }
    } catch (error) {
      console.error('Gagal mengambil dashboard stats:', error);
    }
  }, []);

  const getLogLimitForTimeRange = useCallback((timeRange) => {
    switch (timeRange) {
      case '5m':
        return 100;
      case '15m':
        return 300;
      case '30m':
        return 600;
      case '45m':
        return 900;
      case '1h':
        return 120;
      case '6h':
        return 720;
      case '24h':
        return 1440;
      case '7d':
        return 10080;
      case '30d':
        return 43200;
      default:
        return 100;
    }
  }, []);

  const fetchTargetLogs = useCallback(async (targetId, filters) => {
    if (!targetId) return;

    const fetchGen = ++targetLogsFetchGenRef.current;
    setTargetLogs([]);
    setTargetLogsLoading(true);
    try {
      const params = {
        limit: getLogLimitForTimeRange(filters?.timeRange),
        offset: 0,
      };

      if (filters?.timeRange) params.timeRange = filters.timeRange;
      if (filters?.startDate && filters?.endDate) {
        params.startDate = filters.startDate;
        params.endDate = filters.endDate;
      }
      if (filters?.status && filters.status !== 'all') params.status = filters.status;
      if (filters?.latencyMin !== undefined && filters.latencyMin !== null && filters.latencyMin !== '') params.latencyMin = filters.latencyMin;
      if (filters?.latencyMax !== undefined && filters.latencyMax !== null && filters.latencyMax !== '') params.latencyMax = filters.latencyMax;

      const response = await targetsAPI.getTargetLogs(targetId, params);
      if (fetchGen !== targetLogsFetchGenRef.current) {
        return;
      }
      if (response.success) {
        const rows = Array.isArray(response.data) ? response.data : [];
        const tid = Number(targetId);
        setTargetLogs(
          rows.filter((row) =>
            row?.target_id == null || Number(row.target_id) === tid
          )
        );
      } else {
        setTargetLogs([]);
      }
    } catch (error) {
      console.error('Gagal mengambil target logs:', error);
      if (fetchGen === targetLogsFetchGenRef.current) {
        setTargetLogs([]);
      }
    } finally {
      if (fetchGen === targetLogsFetchGenRef.current) {
        setTargetLogsLoading(false);
      }
    }
  }, [getLogLimitForTimeRange]);

  const findTargetRow = useCallback((targetId) => {
    const id = Number(targetId);
    return targetsRef.current.find((t) => t.id === targetId || t.id === id);
  }, []);

  const pushTargetStatusNotification = useCallback((data, options = {}) => {
    const { showPopup = true, playSound = true } = options;
    if (!data?.targetId) return;
    if (!shouldNotifyTargetStatus(data)) return;

    const targetRow = findTargetRow(data.targetId);
    const status = String(data.status || '').toUpperCase();
    if (isPhase1FailureStatus('target-status', status)) {
      registerPhase1Incident('target-status', data.targetId, {
        consecutiveThreshold: data.consecutiveThreshold ?? targetRow?.consecutive_timeout_threshold,
        checkIntervalSec: targetRow?.check_interval ?? targetRow?.interval,
      });
    }

    const popupOptions = {
      consecutiveThreshold: data.consecutiveThreshold ?? targetRow?.consecutive_timeout_threshold,
      checkIntervalSec: targetRow?.check_interval ?? targetRow?.interval,
    };
    const allowPopup = showPopup && canShowPhase1Popup('target-status', data.targetId);
    const targetName = data.targetName || targetRow?.name || data.host;
    const isRecovery = Boolean(
      data.isRecovery
      || (data.status === 'UP' && data.previousStatus && data.previousStatus !== 'UP')
    );
    const popupData = {
      ...data,
      timestamp: data.timestamp ? toLocalTime(data.timestamp) : null,
    };
    const popupDesc = buildTargetStatusPopupDescription(
      popupData,
      targetRow || {},
      { isRecovery }
    );
    const popupDescNode = (
      <TargetStatusPopupDescription
        data={popupData}
        target={targetRow || {}}
        isRecovery={isRecovery}
      />
    );

    let severity = 'MEDIUM';
    if (data.status === 'DOWN') severity = 'CRITICAL';
    else if (data.status === 'TIMEOUT') severity = 'HIGH';

    const panelAlert = {
      id: data.alertId ? `server-${data.alertId}` : `target-${data.targetId}-${data.status}`,
      type: 'target-status',
      severity,
      title: formatTargetAlertTitle(data.status, targetName, isRecovery),
      description: popupDesc,
      timestamp: data.timestamp || new Date().toISOString(),
      data: { ...data, targetId: data.targetId },
      autoDismiss: isRecovery,
      serverId: data.alertId ?? null,
      isServerAlert: Boolean(data.alertId),
    };

    setActiveAlerts((prev) => (
      isRecovery
        ? removeActiveAlertsForTargetId(prev, data.targetId, ['target-status'])
        : upsertAlert(prev, panelAlert)
    ));

    const isSameTargetInModal = statsModalVisibleRef.current
      && (selectedTargetIdRef.current === data.targetId
        || selectedTargetIdRef.current === Number(data.targetId));
    const skipPopup = isSameTargetInModal && statsPopupHiddenRef.current;

    const popupKey = `target-alert-${data.targetId}`;
    const popupTitle = formatTargetPopupTitle(data.status, targetName, isRecovery);
    let popupType = 'info';
    if (data.status === 'DOWN') popupType = 'error';
    else if (data.status === 'TIMEOUT') popupType = 'warning';
    else if (isRecovery) popupType = 'success';

    lastTargetPopupRef.current[data.targetId] = {
      popupKey,
      popupType,
      popupTitle,
      popupDesc,
      popupDescNode,
      duration: data.status === 'DOWN' ? 0 : isRecovery ? 8 : 12,
    };

    if (allowPopup && !skipPopup) {
      recordPhase1PopupShown('target-status', data.targetId, popupOptions);
      showGroupedPopup(popupKey, popupType, {
        message: popupTitle,
        description: popupDescNode,
        duration: data.status === 'DOWN' ? 0 : isRecovery ? 8 : 12,
        onClick: () => {
          notification.destroy(popupKey);
          openTargetDetailModal(data.targetId);
        },
      });
    }

    if (playSound) {
      if (isRecovery) {
        playAlertSound('LOW');
      } else if (data.status === 'DOWN') {
        playAlertSound(severity);
      }
    }

    scheduleRefreshActiveAlerts();
  }, [findTargetRow, showGroupedPopup, openTargetDetailModal, playAlertSound, scheduleRefreshActiveAlerts]);

  const handleStatusUpdate = useCallback((data) => {
    targetsRef.current = patchTargetInList(targetsRef.current, data);

    setTargets((prevTargets) => patchTargetInList(prevTargets, data));

    const normalized = String(data.status || '').toUpperCase();
    const isRecoveryStatus = normalized === 'UP' || normalized === 'OK';
    const statusChanged = data.previousStatus != null && data.previousStatus !== data.status;

    if (data.targetId && isRecoveryStatus) {
      clearAcknowledgedKeysForTarget(data.targetId);
      clearPhase1OnTargetRecovery(data.targetId);
      setActiveAlerts((prev) => removeActiveAlertsForTargetId(prev, data.targetId, ['target-status']));
      scheduleRefreshActiveAlerts();
    } else if (statusChanged && data.targetId) {
      clearAcknowledgedKeysForTarget(data.targetId);
    }

    if (!statusChanged || !data.targetId) return;
    if (!shouldNotifyTargetStatus(data)) return;

    // Sound handled by target-status-alert (aligned with email); avoid double beep
    pushTargetStatusNotification(data, { showPopup: true, playSound: false });
  }, [pushTargetStatusNotification]);

  const handleTargetCreated = useCallback((target) => {
    setTargets(prev => [target, ...prev]);
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const handleTargetUpdated = useCallback((target) => {
    setTargets(prevTargets =>
      prevTargets.map(t => t.id === target.id ? target : t)
    );
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const handleTargetDeleted = useCallback(({ targetId }) => {
    setTargets(prevTargets => prevTargets.filter(t => t.id !== targetId));
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // Monitored item alert (process/service/PM2 status change)
  const handleMonitoredItemAlert = useCallback((data) => {
    const agentId = data.agent_id ?? data.agentId;
    const itemName = data.item_name ?? data.itemName;

    if (data.is_recovery) {
      setActiveAlerts((prev) => removeMonitoredItemAlerts(prev, agentId, itemName));
      if (alertPopupEnabled) {
        const itemLabel = monitoredItemTypeLabel(data.item_type);
        notification.success({
          message: `Monitor Recovery: ${data.agent_name}`,
          description: `${itemLabel} ${data.item_type} "${itemName}" recovered to ${data.new_status}`,
          duration: 8,
          placement: 'topRight',
        });
      }
      return;
    }

    const itemLabel = monitoredItemTypeLabel(data.item_type);
    const newAlert = {
      id: `monitored-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'monitored-item',
      severity: 'HIGH',
      title: `Monitor Alert: ${data.agent_name}`,
      description: `${itemLabel} ${data.item_type} "${itemName}"\n${data.previous_status} -> ${data.new_status}${data.error_message ? '\n' + data.error_message : ''}`,
      timestamp: new Date().toISOString(),
      data,
      autoDismiss: false,
    };
    setActiveAlerts(prev => upsertAlert(prev, newAlert));

    playAlertSound('HIGH');
    if (alertPopupEnabled) {
      notification.error({
        message: newAlert.title,
        description: newAlert.description,
        duration: 30,
        placement: 'topRight',
      });
    }
  }, []);

  const handleHardwareHealthUpdate = useCallback((data) => {
    clearHardwareAckWhenHealthy(data);
    const issues = data?.summary?.issues;
    const overall = data?.summary?.overall;
    const healthy = (
      (Array.isArray(issues) && issues.length === 0)
      || overall === 'Healthy'
      || overall === 'OK'
    );
    if (healthy) {
      const agentId = data.agent_id ?? data.agentId;
      clearPhase1OnHardwareRecovery(agentId);
      if (agentId != null) {
        setActiveAlerts((prev) => removeActiveAlertsForHardwareAgent(prev, agentId));
      }
    }
  }, []);

  // Hardware health alert
  const handleHardwareAlert = useCallback((data) => {
    const agentId = data.agent_id ?? data.agentId;
    registerPhase1Incident('hardware', agentId, { checkIntervalSec: 300 });

    const issues = data.summary?.issues || [];
    const criticalCount = issues.filter(i => i.severity === 'critical').length;
    const warningCount = issues.filter(i => i.severity === 'warning').length;
    const severity = criticalCount > 0 ? 'CRITICAL' : warningCount > 0 ? 'HIGH' : 'MEDIUM';

    const issueDetails = issues.slice(0, 3).map(i =>
      `${severityIssuePrefix(i.severity)} ${i.component}: ${i.message}`
    ).join('\n');
    const moreText = issues.length > 3 ? `\n+${issues.length - 3} more issues` : '';

    const newAlert = {
      id: `hardware-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'hardware',
      severity,
      title: `Hardware Alert: ${data.agent_name}`,
      description: `${criticalCount} critical, ${warningCount} warning\n${issueDetails}${moreText}`,
      timestamp: data.timestamp || new Date().toISOString(),
      data,
      autoDismiss: false,
    };
    setActiveAlerts(prev => upsertAlert(prev, newAlert));

    if (!canShowPhase1Popup('hardware', agentId)) return;
    recordPhase1PopupShown('hardware', agentId, { checkIntervalSec: 300 });

    playAlertSound(severity);
    showGroupedPopup(`hardware-alert-${agentId || 'unknown'}`, 'error', {
      message: newAlert.title,
      description: newAlert.description,
      duration: 30,
    });
  }, [showGroupedPopup, playAlertSound]);

  const handleAgentResourcesUpdate = useCallback((data) => {
    setActiveAlerts((prev) => applyResourceMetricsToAlerts(prev, data));
  }, []);

  const handleAgentEventAnomalyUpdate = useCallback((data) => {
    if (!isEventAnomalyClear(data)) return;
    const agentId = data.agent_id ?? data.agentId;
    setActiveAlerts((prev) => removeActiveAlertsForAgent(prev, agentId, ['event-anomaly']));
  }, []);

  // Event anomaly alert
  const handleEventAnomalyAlert = useCallback((data) => {
    const severity = data.total_errors > 0 ? 'HIGH' : data.total_warnings > 0 ? 'MEDIUM' : 'LOW';
    const eventPrefix = data.total_errors > 0 ? '[ERR]' : '[WARN]';
    const eventSummary = data.errors?.slice(0, 2).map(e =>
      `${eventPrefix} ${e.source || 'Event'}: ${e.message}`
    ).join('\n') || '';
    const moreText = (data.total_errors + data.total_warnings) > 2 ? `\n+${data.total_errors + data.total_warnings - 2} more events` : '';

    const newAlert = {
      id: `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'event-anomaly',
      severity,
      title: `Event Anomaly: ${data.agent_name}`,
      description: `${data.total_errors} errors, ${data.total_warnings} warnings\n${eventSummary}${moreText}`,
      timestamp: new Date().toISOString(),
      data,
      autoDismiss: severity === 'LOW',
    };
    setActiveAlerts(prev => upsertAlert(prev, newAlert));

    playAlertSound(severity);
    if (severity !== 'LOW' && alertPopupEnabled) {
      notification.warning({
        message: newAlert.title,
        description: newAlert.description,
        duration: severity === 'HIGH' ? 0 : 8,
        placement: 'topRight',
      });
    }
  }, []);

  // Backend target alert — same moment as email (ISSUE-006); always update panel + popup
  const handleTargetStatusAlert = useCallback((data) => {
    if (!shouldNotifyTargetStatus(data)) return;

    const shouldPlaySound = data.emailThresholdReached
      || data.emailConsecutiveFailures === 1
      || data.consecutiveTimeoutThresholdReached
      || data.status === 'DOWN';

    pushTargetStatusNotification(data, {
      showPopup: true,
      playSound: shouldPlaySound,
    });
  }, [pushTargetStatusNotification]);

  const applyInfluxRecovery = useCallback((data) => {
    clearInfluxAckOnStatusChange(data);
    const targetId = data.influxTargetId ?? data.targetId;
    if (targetId != null) {
      influxStatusRef.current[String(targetId)] = data.status ?? 'UP';
    }
    if (!isInfluxUpStatus(data.status)) return;
    clearPhase1OnInfluxRecovery(targetId);
    if (targetId != null) {
      setActiveAlerts((prev) => removeActiveAlertsForTargetId(prev, targetId, ['influxdb']));
      scheduleRefreshActiveAlerts();
    }
  }, [scheduleRefreshActiveAlerts]);

  const handleInfluxStatusUpdate = useCallback((data) => {
    applyInfluxRecovery(data);
  }, [applyInfluxRecovery]);

  // InfluxDB alert
  const handleInfluxAlert = useCallback((data) => {
    const targetId = data.targetId ?? data.influxTargetId;
    if (targetId != null && isInfluxUpStatus(data.status)) {
      applyInfluxRecovery(data);
      return;
    }

    if (!shouldNotifyInfluxStatus(data.status)) return;
    prepareInfluxAlertAck(data);

    if (targetId != null) {
      influxStatusRef.current[String(targetId)] = data.status;
    }
    registerPhase1Incident('influxdb', targetId, {
      checkIntervalSec: data.checkInterval ?? data.check_interval ?? 60,
      consecutiveThreshold: data.consecutiveThreshold ?? 5,
    });

    const popupMeta = {
      checkIntervalSec: data.checkInterval ?? data.check_interval ?? 60,
      consecutiveThreshold: data.consecutiveThreshold ?? 5,
    };

    let severity = data.severity || 'MEDIUM';
    if (data.status === 'DOWN' || data.status === 'ERROR') severity = 'CRITICAL';
    else if (data.status === 'ANOMALY') {
      severity = data.severity || (
        (data.anomalyDetails || []).some((a) => a.method === 'threshold') ? 'CRITICAL' : 'HIGH'
      );
    }

    const detailText = data.status === 'ANOMALY'
      ? formatInfluxAnomalyDescription(data)
      : (data.error || 'Query failed');

    const newAlert = {
      id: data.alertId ? `server-${data.alertId}` : `influx-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'influxdb',
      severity,
      title: formatInfluxAlertTitle(
        data.targetName || data.targetId,
        data.status === 'ANOMALY'
      ),
      description: detailText,
      timestamp: data.timestamp || new Date().toISOString(),
      data: {
        ...data,
        targetId: data.targetId ?? data.influxTargetId,
        influxTargetId: data.influxTargetId ?? data.targetId,
        status: data.status,
      },
      autoDismiss: false,
      serverId: data.alertId ?? null,
      isServerAlert: Boolean(data.alertId),
    };
    setActiveAlerts(prev => upsertAlert(prev, newAlert));

    if (!canShowPhase1Popup('influxdb', targetId)) return;
    recordPhase1PopupShown('influxdb', targetId, popupMeta);

    const influxKey = `influx-alert-${data.targetId || 'unknown'}`;

    const influxPopupTitle = formatInfluxPopupTitle(
      data.targetName || data.targetId,
      data.status === 'ANOMALY'
    );

    if (data.status === 'ANOMALY') {
      playAlertSound(severity);
      showGroupedPopup(influxKey, 'warning', {
        message: influxPopupTitle,
        description: newAlert.description,
        duration: 20,
      });
    } else if (data.status === 'DOWN' || data.status === 'ERROR') {
      playAlertSound('CRITICAL');
      showGroupedPopup(influxKey, 'error', {
        message: influxPopupTitle,
        description: newAlert.description,
        duration: 20,
      });
    }
  }, [applyInfluxRecovery, showGroupedPopup, playAlertSound]);

  // Acknowledge/dismiss an alert
  const acknowledgeAlert = useCallback((alertId) => {
    setActiveAlerts((prev) => {
      const alert = prev.find((a) => a.id === alertId);
      if (!alert) return prev;
      if (PHASE1_ALERT_TYPES.has(alert.type)) {
        markPhase1IncidentAcknowledged(alert);
        const ackReq = phase1AckRequestFromAlert(alert);
        if (ackReq) {
          alertsAPI.acknowledgeIncident(ackReq.type, ackReq.targetId).catch((err) => {
            console.error('Failed to sync phase1 acknowledge:', err);
          });
        }
      } else {
        const key = getAlertDedupeKey(alert);
        acknowledgeAlertKey(key);
      }
      const targetId = alert.data?.targetId ?? alert.targetId;
      if (targetId != null) {
        notification.destroy(`target-alert-${targetId}`);
        delete lastTargetPopupRef.current[targetId];
      }
      if (alert.type === 'influxdb' && targetId != null) {
        notification.destroy(`influx-alert-${targetId}`);
      }
      const agentId = alert.data?.agent_id ?? alert.data?.agentId ?? alert.agentId;
      if (alert.type === 'hardware' && agentId != null) {
        notification.destroy(`hardware-alert-${agentId}`);
      }
      if (alert.serverId) {
        alertsAPI.acknowledge(alert.serverId).catch((err) => {
          console.error('Failed to acknowledge server alert:', err);
        });
      }
      setAckUIVersion((v) => v + 1);
      return prev;
    });
  }, []);

  // Close all popup notifications
  const closeAllNotifications = useCallback(() => {
    notification.destroy();
  }, []);

  // Acknowledge all alerts that still show an Ack button
  const acknowledgeAllAlerts = useCallback(async () => {
    setActiveAlerts((prev) => {
      const pending = prev.filter(shouldShowAckButton);
      markAllPhase1IncidentsAcknowledged(pending);
      pending.forEach((alert) => {
        if (PHASE1_ALERT_TYPES.has(alert.type)) {
          const ackReq = phase1AckRequestFromAlert(alert);
          if (ackReq) {
            alertsAPI.acknowledgeIncident(ackReq.type, ackReq.targetId).catch(() => {});
          }
        } else {
          acknowledgeAlertKey(getAlertDedupeKey(alert));
        }
      });
      setAckUIVersion((v) => v + 1);
      return prev;
    });
    try {
      await alertsAPI.acknowledgeAll(user?.username || 'unknown');
    } catch (err) {
      console.error('Failed to acknowledge all server alerts:', err);
    }
    notification.destroy();
  }, [user]);

  const pendingAckCount = countPendingAcknowledgements(activeAlerts);

  useEffect(() => {
    fetchTargets();
    fetchDashboardStats();

    const removeSocketConnect = socketService.onConnect(() => {
      socketService.subscribeToTargets();
    });
    if (socketService.isConnected()) {
      socketService.subscribeToTargets();
    }

    socketService.onStatusUpdate(handleStatusUpdate);
    socketService.onTargetCreated(handleTargetCreated);
    socketService.onTargetUpdated(handleTargetUpdated);
    socketService.onTargetDeleted(handleTargetDeleted);

    const handleResourceAlert = (data) => {
      const agentId = data.agent_id ?? data.agentId;
      const alertDesc = data.alerts.map(a =>
        `${a.type.toUpperCase()}: ${a.value.toFixed(1)}% (threshold: ${a.threshold}%)`
      ).join(', ');

      const severity = 'HIGH';
      const newAlert = {
        id: `resource-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'resource',
        severity,
        title: `Resource Alert: ${data.agent_name}`,
        description: alertDesc,
        timestamp: new Date().toISOString(),
        targetId: agentId,
        data: { ...data, agent_id: agentId, agentId },
        autoDismiss: false,
      };
      setActiveAlerts(prev => upsertAlert(prev, newAlert));

      notification.warning({
        message: newAlert.title,
        description: newAlert.description,
        duration: 30,
        placement: 'topRight',
      });
    };
    socketService.on('resource-threshold-alert', handleResourceAlert);
    socketService.onAgentResourcesUpdate(handleAgentResourcesUpdate);

    socketService.on('monitored-item-alert', handleMonitoredItemAlert);
    socketService.on('agent-hardware-update', handleHardwareHealthUpdate);
    socketService.on('hardware-health-alert', handleHardwareAlert);
    socketService.on('event-anomaly-alert', handleEventAnomalyAlert);
    socketService.on('agent-event-anomaly', handleAgentEventAnomalyUpdate);
    socketService.on('target-status-alert', handleTargetStatusAlert);
    socketService.on('influx-status-update', handleInfluxStatusUpdate);
    socketService.on('influxdb-alert', handleInfluxAlert);

    // Listen for settings changes (ringtone mode, selected ringtone) from other users
    const handleSettingsChanged = (data) => {
      if (data.key === 'ringtone_mode') {
        localStorage.setItem('pingpulse_ringtone_mode', data.value);
        message.info(`Alert mode changed to: ${data.value === 'random' ? 'Random Ringtone' : 'Fixed'}`);
      } else if (data.key === 'selected_ringtone') {
        localStorage.setItem('pingpulse_selected_ringtone', data.value);
      }
    };
    socketService.on('settings-changed', handleSettingsChanged);

    return () => {
      removeSocketConnect();
      socketService.offStatusUpdate(handleStatusUpdate);
      socketService.offTargetCreated(handleTargetCreated);
      socketService.offTargetUpdated(handleTargetUpdated);
      socketService.offTargetDeleted(handleTargetDeleted);
      socketService.offTargetStatusAlert(handleTargetStatusAlert);
      socketService.off('resource-threshold-alert', handleResourceAlert);
      socketService.off('agent-resources-update', handleAgentResourcesUpdate);
      socketService.off('monitored-item-alert', handleMonitoredItemAlert);
      socketService.off('agent-hardware-update', handleHardwareHealthUpdate);
      socketService.off('hardware-health-alert', handleHardwareAlert);
      socketService.off('event-anomaly-alert', handleEventAnomalyAlert);
      socketService.off('agent-event-anomaly', handleAgentEventAnomalyUpdate);
      socketService.off('influxdb-alert', handleInfluxAlert);
      socketService.off('influx-status-update', handleInfluxStatusUpdate);
      socketService.off('settings-changed', handleSettingsChanged);
    };
  }, [fetchTargets, fetchDashboardStats, handleStatusUpdate, handleTargetCreated, handleTargetUpdated, handleTargetDeleted, handleMonitoredItemAlert, handleHardwareHealthUpdate, handleHardwareAlert, handleEventAnomalyAlert, handleAgentResourcesUpdate, handleAgentEventAnomalyUpdate, handleTargetStatusAlert, handleInfluxStatusUpdate, handleInfluxAlert, message]);

  const handleAddTarget = () => {
    setEditingTarget(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditTarget = (target) => {
    setEditingTarget(target);
    setModalVisible(true);

    // Parse agent_id into agent_ids array (comma-separated string to array)
    let agentIdsArray = [];
    if (target.agent_id && typeof target.agent_id === 'string') {
      agentIdsArray = target.agent_id.split(',').map(id => id.trim()).filter(id => id.length > 0);
    }

    const editValues = {
      name: target.name,
      host: target.host,
      target_type: target.target_type,
      port: target.port,
      interval: target.interval,
      is_active: target.is_active,
      email_notification: target.email_notification,
      webhook_url: target.webhook_url || '',
      agent_ids: agentIdsArray,
      options: target.options || {},
    };

    form.setFieldsValue(editValues);
  };

  const handleDeleteTarget = async (target) => {
    Modal.confirm({
      title: 'Hapus Target',
      content: `Apakah Anda yakin ingin menghapus target "${target.name}"?`,
      okText: 'Hapus',
      okButtonProps: { danger: true },
      cancelText: 'Batal',
      onOk: async () => {
        try {
          await targetsAPI.deleteTarget(target.id);
          message.success('Target berhasil dihapus');
        } catch (error) {
          message.error('Gagal menghapus target');
        }
      }
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      // Convert agent_ids array to comma-separated string for backend
      let agentIdValue = null;
      if (values.agent_ids && Array.isArray(values.agent_ids) && values.agent_ids.length > 0) {
        agentIdValue = values.agent_ids.join(',');
      }

      const payload = {
        ...values,
        agent_id: agentIdValue,
      };
      // Remove agent_ids from payload (backend uses comma-separated string)
      delete payload.agent_ids;

      if (editingTarget) {
        await targetsAPI.updateTarget(editingTarget.id, payload);
        message.success('Target berhasil diperbarui');
      } else {
        await targetsAPI.createTarget(payload);
        message.success('Target berhasil ditambahkan');
      }
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Gagal menyimpan target');
    }
  };

  const showStatsModal = (target) => {
    setSelectedTarget(target);
    setStatsModalVisible(true);
  };

  const toggleStatsTargetPopup = useCallback(() => {
    if (!selectedTarget?.id) return;
    const targetId = selectedTarget.id;
    const popupKey = `target-alert-${targetId}`;

    if (statsPopupHiddenRef.current) {
      setStatsPopupHidden(false);
      statsPopupHiddenRef.current = false;
      const cached = lastTargetPopupRef.current[targetId];
      if (cached) {
        showGroupedPopup(cached.popupKey, cached.popupType, {
          message: cached.popupTitle,
          description: cached.popupDescNode || cached.popupDesc,
          duration: cached.duration,
          onClick: () => {
            notification.destroy(popupKey);
          },
        });
      }
      return;
    }

    notification.destroy(popupKey);
    setStatsPopupHidden(true);
    statsPopupHiddenRef.current = true;
  }, [selectedTarget?.id, showGroupedPopup]);

  useEffect(() => {
    if (!statsModalVisible || !selectedTarget?.id) return;
    fetchTargetLogs(selectedTarget.id, currentFilters);
  }, [statsModalVisible, selectedTarget?.id, currentFilters, fetchTargetLogs]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'UP':
        return 'green';
      case 'DOWN':
        return 'red';
      case 'TIMEOUT':
        return 'orange';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'last_status',
      key: 'status',
      width: 88,
      align: 'center',
      fixed: 'left',
      render: (status, record) => {
        const statusTag = (
          <Tag color={getStatusColor(status)}>
            {status || 'UNKNOWN'}
          </Tag>
        );

        const tooltipContent = (
          <div>
            <div><strong>Message:</strong> {record.last_message || record.last_error || 'No message'}</div>
            {record.last_check && (
              <>
                <div><strong>Time (Local):</strong> {toLocalTimeWithTZ(record.last_check)}</div>
                <div><strong>Time (UTC):</strong> {record.last_check}</div>
              </>
            )}
          </div>
        );

        return (
          <Tooltip title={tooltipContent} placement="topLeft">
            {statusTag}
          </Tooltip>
        );
      },
    },
    {
      title: 'Consecutive',
      key: 'consecutive_failures',
      width: 96,
      align: 'center',
      render: (_, record) => {
        const threshold = record.consecutive_timeout_threshold ?? 5;
        const rawCount = record.consecutive_failures ?? 0;
        const count = Math.min(rawCount, threshold);
        if (record.last_status === 'UP' || !record.last_status) {
          return <span className="target-table-muted">-</span>;
        }
        const atThreshold = count >= threshold;
        return (
          <Tooltip title={`Consecutive timeout/failure count before status DOWN (threshold: ${threshold})`}>
            <Tag color={atThreshold ? 'red' : count > 0 ? 'orange' : 'default'}>
              {count}/{threshold}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Nama',
      dataIndex: 'name',
      key: 'name',
      width: 140,
      ellipsis: true,
      render: (name) => <CellEllipsis text={name} width={130} />,
    },
    {
      title: 'Type',
      dataIndex: 'target_type',
      key: 'target_type',
      width: 80,
      align: 'center',
      render: (type) => <Tag color="blue" style={{ margin: 0 }}>{type}</Tag>,
    },
    {
      title: 'Host',
      dataIndex: 'host',
      key: 'host',
      width: 130,
      ellipsis: true,
      render: (host) => <CellEllipsis text={host} width={120} />,
    },
    {
      title: 'Port',
      dataIndex: 'port',
      key: 'port',
      width: 64,
      align: 'center',
      render: (port) => port || <span className="target-table-muted">-</span>,
    },
    {
      title: 'Interval',
      dataIndex: 'interval',
      key: 'interval',
      width: 72,
      align: 'center',
      render: (interval) => `${interval}s`,
    },
    {
      title: 'Latency',
      dataIndex: 'last_latency',
      key: 'latency',
      width: 72,
      align: 'center',
      render: (latency) => (latency != null ? `${latency}ms` : <span className="target-table-muted">-</span>),
    },
    {
      title: 'Last Check',
      dataIndex: 'last_check',
      key: 'last_check',
      width: 168,
      render: (timestamp) => {
        if (!timestamp) return <span className="target-table-muted">-</span>;
        const local = toLocalTime(timestamp);
        return (
          <Tooltip title={`UTC: ${timestamp}`}>
            <span className="target-table-nowrap">{local}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Error Message',
      dataIndex: 'last_error',
      key: 'last_error',
      width: 240,
      render: (error, record) => {
        if (record.last_status === 'UP' || !record.last_status) {
          return <span className="target-table-muted">-</span>;
        }
        const msg = error || record.last_message || 'No error details';
        return <CellEllipsis text={msg} width={228} style={{ color: '#cf1322', fontSize: 12 }} />;
      },
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 72,
      align: 'center',
      render: (active) => (
        <Tag color={active ? 'blue' : 'default'}>
          {active ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Agent',
      dataIndex: 'agent_id',
      key: 'agent_id',
      width: 96,
      align: 'center',
      render: (agentId) => {
        if (!agentId) {
          return <Tag color="default">Central</Tag>;
        }
        const agents = agentId.split(',').map(id => id.trim());
        return (
          <Space wrap>
            {agents.map((agent, index) => (
              <Tag key={index} color="purple" style={{ marginBottom: 2 }}>
                {agent.length > 12 ? agent.substring(0, 12) + '...' : agent}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 108,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space size={0} className="target-table-actions">
          {user?.role === 'admin' && (
            <Tooltip title="Edit">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditTarget(record)}
              />
            </Tooltip>
          )}
          {user?.role === 'admin' && (
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                danger
                onClick={() => handleDeleteTarget(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="Stats">
            <Button
              type="text"
              size="small"
              icon={<LineChartOutlined />}
              onClick={() => showStatsModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const logColumns = [
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp, record) => record.local_timestamp || toLocalTimeWithTZ(timestamp)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>
    },
    {
      title: 'Latency',
      dataIndex: 'latency',
      key: 'latency',
      width: 110,
      render: (latency) => (latency === null || latency === undefined ? '-' : `${latency}ms`)
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 140,
      render: (method) => method || '-'
    },
    {
      title: 'Message',
      key: 'message',
      render: (_, record) => record.message || record.error_message || '-'
    }
  ];

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: onLogout
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 104, background: 'linear-gradient(135deg, #39436d 0%, #428bc0 100%)' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img src="/logo.png" alt="PingPulse Logo" style={{ width: 80, height: 80, marginRight: 12 }} />
          <h1 style={{ color: 'white', margin: 0 }}>PingPulse</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Alert Bell */}
          <Tooltip title={activeAlerts.length > 0 ? `${activeAlerts.length} active alerts` : 'No active alerts'}>
            <Badge count={countAlertBadge(activeAlerts)} size="small" offset={[-2, 2]} overflowCount={99}>
              <Button
                type="text"
                icon={<BellOutlined />}
                onClick={() => setAlertPanelVisible(true)}
                style={{ color: 'white', fontSize: 20 }}
              />
            </Badge>
          </Tooltip>
          {/* Sound Toggle */}
          <Tooltip title={alertSoundEnabled ? 'Mute alert sounds' : 'Alert sounds muted'}>
            <Button
              type="text"
              icon={alertSoundEnabled ? <SoundOutlined /> : <MutedOutlined />}
              onClick={() => setAlertSoundEnabled(!alertSoundEnabled)}
              style={{ color: alertSoundEnabled ? 'white' : '#ff4d4f', fontSize: 18 }}
            />
          </Tooltip>
          {/* Popup Toggle */}
          <Tooltip title={alertPopupEnabled ? 'Disable popup notifications' : 'Popup notifications disabled'}>
            <Button
              type="text"
              icon={alertPopupEnabled ? <NotificationOutlined /> : <BellOutlined />}
              onClick={() => setAlertPopupEnabled(!alertPopupEnabled)}
              style={{ color: alertPopupEnabled ? 'white' : '#ff4d4f', fontSize: 18 }}
            />
          </Tooltip>
          {/* Do not disturb (mute popups temporarily) */}
          <Tooltip title={isDndActive ? `DND until ${new Date(dndUntil).toLocaleTimeString('id-ID')}` : 'Mute popups 1 hour (Do Not Disturb)'}>
            <Button
              type="text"
              icon={<PauseCircleOutlined />}
              onClick={() => (isDndActive ? disableDoNotDisturb() : enableDoNotDisturb(60))}
              style={{ color: isDndActive ? '#faad14' : 'white', fontSize: 18 }}
            />
          </Tooltip>
          {/* Test Alert Button */}
          <Tooltip title="Test notification + sound">
            <Button
              type="text"
              icon={<ApiOutlined />}
              onClick={() => {
                playAlertSound('CRITICAL');
                if (alertPopupEnabled) {
                  notification.error({
                    message: '🔴 Test Alert: CRITICAL',
                    description: 'This is a test notification with sound. Click to dismiss.',
                    duration: 30,
                    placement: 'topRight',
                  });
                }
                // Also add to active alerts panel
                const newAlert = {
                  id: `test-${Date.now()}`,
                  type: 'test',
                  severity: 'CRITICAL',
                  title: '🔴 Test Alert: CRITICAL',
                  description: 'This is a test notification with sound.',
                  timestamp: new Date().toISOString(),
                  data: {},
                  autoDismiss: false,
                };
                setActiveAlerts(prev => upsertAlert(prev, newAlert));
              }}
              style={{ color: 'white', fontSize: 18 }}
            />
          </Tooltip>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} style={{ marginRight: 8 }} />
              <span style={{ color: 'white' }}>{user?.username}</span>
            </div>
          </Dropdown>
        </div>
      </Header>
      <Layout>
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          width={200}
          style={{ background: '#fff' }}
        >
          <Menu
            mode="inline"
            selectedKeys={[selectedMenu]}
            style={{ height: '100%', borderRight: 0 }}
            onClick={handleMenuClick}
            items={[
              {
                key: 'overview',
                icon: <DashboardOutlined />,
                label: 'Overview',
              },
              {
                key: 'monitor',
                icon: <MonitorOutlined />,
                label: 'Monitor',
              },
              ...(user?.role === 'admin' ? [
                {
                  key: 'settings-section',
                  icon: <SettingOutlined />,
                  label: 'Settings',
                  children: [
                    {
                      key: 'settings',
                      icon: <SettingOutlined />,
                      label: 'General Settings',
                    },
                    {
                      key: 'email-config',
                      icon: <MailOutlined />,
                      label: 'Email Configuration',
                    },
                    {
                      key: 'email-notifications',
                      icon: <MailOutlined />,
                      label: 'Email Notifications',
                    },
                    {
                      key: 'user-management',
                      icon: <TeamOutlined />,
                      label: 'User Management',
                    },
                  ],
                },
                {
                  key: 'agents',
                  icon: <ApiOutlined />,
                  label: 'Agents',
                },
                {
                  key: 'influxdb',
                  icon: <DatabaseOutlined />,
                  label: 'InfluxDB Targets',
                },
              ] : []),
              {
                key: 'change-password',
                icon: <LockOutlined />,
                label: 'Change Password',
              },
            ]}
          />
        </Sider>
        <Layout style={{ padding: '24px' }}>
          <Content>
            {selectedMenu === 'monitor' && (<>
              <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                <Col xs={12} sm={8} md={4}>
                  <Card
                    size="small"
                    styles={{ body: MONITOR_CARD_BODY }}
                    style={monitorFilterCardStyle(!monitorStatusFilter, '#1890ff')}
                    hoverable
                    onClick={() => setMonitorStatusFilter(null)}
                  >
                    <Statistic
                      title="Targets"
                      value={dashboardStats?.totalTargets ?? targets.length}
                      prefix={<WifiOutlined />}
                      valueStyle={{ fontSize: 24 }}
                    />
                    {monitorFilterActiveDot(!monitorStatusFilter, '#1890ff')}
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card
                    size="small"
                    styles={{ body: MONITOR_CARD_BODY }}
                    style={monitorFilterCardStyle(monitorStatusFilter === 'ACTIVE', '#1890ff')}
                    hoverable
                    onClick={() => setMonitorStatusFilter(monitorStatusFilter === 'ACTIVE' ? null : 'ACTIVE')}
                  >
                    <Statistic
                      title="Active"
                      value={dashboardStats?.activeTargets ?? targets.filter(isActiveMonitorTarget).length}
                      valueStyle={{ color: '#1890ff', fontSize: 24 }}
                      prefix={<MonitorOutlined />}
                    />
                    {monitorFilterActiveDot(monitorStatusFilter === 'ACTIVE', '#1890ff')}
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card
                    size="small"
                    styles={{ body: MONITOR_CARD_BODY }}
                    style={monitorFilterCardStyle(monitorStatusFilter === 'UP', '#52c41a')}
                    hoverable
                    onClick={() => setMonitorStatusFilter(monitorStatusFilter === 'UP' ? null : 'UP')}
                  >
                    <Statistic
                      title="UP"
                      value={targets.filter((t) => isActiveMonitorTarget(t) && getMonitorTargetStatus(t) === 'UP').length}
                      valueStyle={{ color: '#52c41a', fontSize: 24 }}
                      prefix={<CheckCircleOutlined />}
                    />
                    {monitorFilterActiveDot(monitorStatusFilter === 'UP', '#52c41a')}
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card
                    size="small"
                    styles={{ body: MONITOR_CARD_BODY }}
                    style={monitorFilterCardStyle(monitorStatusFilter === 'DOWN', '#ff4d4f')}
                    hoverable
                    onClick={() => setMonitorStatusFilter(monitorStatusFilter === 'DOWN' ? null : 'DOWN')}
                  >
                    <Statistic
                      title="DOWN"
                      value={targets.filter((t) => isActiveMonitorTarget(t) && getMonitorTargetStatus(t) === 'DOWN').length}
                      valueStyle={{ color: '#ff4d4f', fontSize: 24 }}
                      prefix={<CloseCircleOutlined />}
                    />
                    {monitorFilterActiveDot(monitorStatusFilter === 'DOWN', '#ff4d4f')}
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card
                    size="small"
                    styles={{ body: MONITOR_CARD_BODY }}
                    style={monitorFilterCardStyle(monitorStatusFilter === 'TIMEOUT', '#faad14')}
                    hoverable
                    onClick={() => setMonitorStatusFilter(monitorStatusFilter === 'TIMEOUT' ? null : 'TIMEOUT')}
                  >
                    <Statistic
                      title="TIMEOUT"
                      value={targets.filter((t) => isActiveMonitorTarget(t) && getMonitorTargetStatus(t) === 'TIMEOUT').length}
                      valueStyle={{ color: '#faad14', fontSize: 24 }}
                      prefix={<ClockCircleOutlined />}
                    />
                    {monitorFilterActiveDot(monitorStatusFilter === 'TIMEOUT', '#faad14')}
                  </Card>
                </Col>
              </Row>

              <Card
                title="Target Monitoring"
                extra={
                  <Space>
                    {monitorStatusFilter && (
                      <Button size="small" onClick={() => setMonitorStatusFilter(null)}>
                        Clear Filter ({monitorStatusFilter === 'ACTIVE' ? 'Active' : monitorStatusFilter})
                      </Button>
                    )}
                    <Input
                      placeholder="Search target..."
                      prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                      value={targetSearch}
                      onChange={(e) => setTargetSearch(e.target.value)}
                      allowClear
                      style={{ width: 220 }}
                    />
                    <Button icon={<ReloadOutlined />} onClick={fetchTargets}>Refresh</Button>
                    {user?.role === 'admin' && (
                      <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTarget}>
                        Add Target
                      </Button>
                    )}
                  </Space>
                }
              >
                <Table
                  className="target-table"
                  size="small"
                  columns={columns}
                  dataSource={targets.filter(t => {
                    if (monitorStatusFilter === 'ACTIVE' && !isActiveMonitorTarget(t)) return false;
                    if (monitorStatusFilter === 'UP' || monitorStatusFilter === 'DOWN' || monitorStatusFilter === 'TIMEOUT') {
                      if (!isActiveMonitorTarget(t)) return false;
                      if (getMonitorTargetStatus(t) !== monitorStatusFilter) return false;
                    }
                    if (!targetSearch) return true;
                    const q = targetSearch.toLowerCase();
                    return (
                      (t.name || '').toLowerCase().includes(q) ||
                      (t.host || '').toLowerCase().includes(q) ||
                      (t.target_type || '').toLowerCase().includes(q) ||
                      (t.last_status || '').toLowerCase().includes(q)
                    );
                  })}
                  rowKey="id"
                  loading={loading}
                  scroll={{ x: 1280 }}
                  pagination={{ pageSize: 10, showTotal: (total) => `${total} targets`, size: 'small' }}
                />
              </Card>
            </>)}

            {selectedMenu === 'email-notifications' && (
              <EmailNotifications />
            )}

            {selectedMenu === 'email-config' && (
              <EmailConfig />
            )}

            {selectedMenu === 'user-management' && (
              <UserManagement />
            )}

            {selectedMenu === 'change-password' && (
              <ChangePassword />
            )}

            {selectedMenu === 'settings' && (
              <Settings />
            )}

            {selectedMenu === 'overview' && (
              <MonitoringOverview />
            )}

            {selectedMenu === 'agents' && user?.role === 'admin' && (
              agentDetailId
                ? <AgentDetail agentId={agentDetailId} embedded />
                : <Agents user={user} />
            )}

            {selectedMenu === 'influxdb' && user?.role === 'admin' && (
              <InfluxDBTargets />
            )}
          </Content>
        </Layout>
      </Layout>

      <Modal
        title={editingTarget ? 'Edit Target' : 'Add Target'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false)
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <TargetForm
          form={form}
          initialValues={editingTarget}
          onFinish={handleModalOk}
          loading={loading}
        />
      </Modal>

      <Modal
        title={
          <Space>
            <span>Target Statistics - {selectedTarget?.name}</span>
            <ExportLogsButton
              targetId={selectedTarget?.id}
              targetName={selectedTarget?.name}
            />
          </Space>
        }
        open={statsModalVisible}
        onCancel={() => setStatsModalVisible(false)}
        footer={(
          <Space>
            <Button
              icon={statsPopupHidden ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              onClick={toggleStatsTargetPopup}
            >
              {statsPopupHidden ? 'Show popup' : 'Hide popup'}
            </Button>
            <Button type="primary" onClick={() => setStatsModalVisible(false)}>
              Close
            </Button>
          </Space>
        )}
        width={1000}
      >
        {selectedTarget && (
          <div>
            <TargetDetailAlerts
              target={selectedTarget}
              visible={statsModalVisible}
              logs={targetLogs}
            />
            <UnifiedFilters
              onFiltersChange={setCurrentFilters}
              loading={loading}
            />
            <TargetStats
              targetId={selectedTarget.id}
              filters={currentFilters}
            />
            <LatencyChart
              targetId={selectedTarget.id}
              filters={currentFilters}
              logs={targetLogs}
              loading={targetLogsLoading}
            />
            <Card title="Logs" style={{ marginTop: 16 }}>
              <Table
                columns={logColumns}
                dataSource={targetLogs}
                rowKey="id"
                loading={targetLogsLoading}
                pagination={{
                  pageSize: 20,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: [20, 50, 100]
                }}
              />
            </Card>
          </div>
        )}
      </Modal>

      {/* Alert Notification Panel */}
      <Modal
        title={
          <Space>
            <BellOutlined />
            <span>Active Alerts ({activeAlerts.length})</span>
          </Space>
        }
        open={alertPanelVisible}
        onCancel={() => setAlertPanelVisible(false)}
        footer={activeAlerts.length > 0 ? [
          <Button key="close-popups" onClick={closeAllNotifications}>
            Close All Popups
          </Button>,
          pendingAckCount > 0 ? (
            <Button key="acknowledge-all" type="primary" onClick={acknowledgeAllAlerts}>
              Acknowledge All ({pendingAckCount})
            </Button>
          ) : null,
          <Button key="close" onClick={() => setAlertPanelVisible(false)}>
            Close
          </Button>
        ].filter(Boolean) : null}
        width={600}
      >
        {activeAlerts.length === 0 ? (
          <Empty description="No active alerts" />
        ) : (
          <List
            dataSource={activeAlerts.slice(0, 10)}
            renderItem={alert => {
              const severityColors = {
                CRITICAL: '#ff4d4f',
                HIGH: '#fa8c16',
                MEDIUM: '#722ed1',
                LOW: '#52c41a',
              };
              const color = severityColors[alert.severity] || '#52c41a';
              const { status, isRecovery, title } = resolveAlertPresentation(alert);
              return (
                <List.Item
                  key={`${alert.id}-${ackUIVersion}`}
                  actions={shouldShowAckButton(alert) ? [
                    <Button key="ack" type="link" size="small" onClick={() => acknowledgeAlert(alert.id)}>
                      Acknowledge
                    </Button>
                  ] : []}
                >
                  <List.Item.Meta
                    avatar={
                      <Space direction="vertical" size={4} align="center">
                        {getAlertStatusIcon(status, isRecovery)}
                        <Tag color={color} style={{ fontSize: 10, padding: '0 6px', margin: 0 }}>
                          {alert.severity}
                        </Tag>
                      </Space>
                    }
                    title={
                      <span style={{ fontWeight: 500 }}>{title}</span>
                    }
                    description={
                      <Space direction="vertical" size={0}>
                        {alert.type === 'target-status' ? (
                          <TargetStatusPopupDescription
                            data={{
                              ...(alert.data || {}),
                              status: alert.data?.status ?? status,
                              errorMessage: alert.data?.errorMessage ?? alert.description,
                              timestamp: alert.data?.timestamp
                                ? toLocalTime(alert.data.timestamp)
                                : toLocalTime(alert.timestamp),
                            }}
                            target={{
                              host: alert.data?.host,
                              port: alert.data?.port,
                              name: alert.data?.targetName,
                            }}
                            isRecovery={isRecovery}
                          />
                        ) : (
                          <span className="alert-panel-description">{
                            alert.type === 'influxdb'
                              ? normalizeInfluxAnomalyMessage(alert.description, alert.data?.anomalyDetails)
                              : normalizeTimeoutDownMessage(alert.description)
                          }</span>
                        )}
                        <span style={{ fontSize: 11, color: '#999' }}>
                          {new Date(alert.timestamp).toLocaleTimeString('id-ID')}
                        </span>
                      </Space>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Modal>
    </Layout>
  );
};

export default Dashboard;
